import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import { Form, Input, Button, Table, Spin } from "antd";
import ListPagination, {
  LIST_DEFAULT_PAGE_SIZE,
  buildPageQuery,
  createPageChangeHandler,
  parseTotalFromPayload,
} from "../../components/ListPagination";
import API, { PLAY_HIS, PLAY_HIS_EXCEL } from "../../network/api";
import { TEXT_DEF } from "../../constant/text";
import {
  buildUsernameDataFilter,
  consumeUsernameFilterForHistory,
} from "../../utils/historyUsernameFilter";

/** @typedef {Record<string, unknown>} PlayRowRaw */

/** «Số lượt đã chơi» — BE: `predictionsCount`; fallback `usedTurns`. */
/** @param {PlayRowRaw} item */
function pickPredictionsPlayedDisplay(item) {
  const pc = item?.predictionsCount;
  if (pc !== undefined && pc !== null && String(pc).trim() !== "") {
    return String(pc).trim();
  }
  const used = item?.usedTurns;
  if (used !== undefined && used !== null && String(used).trim() !== "") {
    return String(Number(used));
  }
  return "—";
}

/** @param {PlayRowRaw} item */
function pickMatchCount(item) {
  const raw =
    item?.totalMatches ??
    item?.matchCount ??
    item?.matchesCount ??
    item?.playedMatches ??
    item?.predictionMatchCount ??
    item?.participatedMatches ??
    item?.matchesPlayed ??
    item?.totalMatchCount ??
    item?.matchParticipationCount ??
    item?.joinMatchCount ??
    item?.joinedMatches;

  if (raw != null && String(raw).trim() !== "") {
    const n = Number(raw);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

function formatMatchCountDisplay(item) {
  const n = pickMatchCount(item);
  return n != null ? String(n) : "—";
}

/**
 * Cột «Tỷ lệ đúng» — chỉ dùng `correctRate` từ BE (số 0–1, 0–100 hoặc chuỗi có %).
 * @param {PlayRowRaw} item
 */
function formatCorrectRateDisplay(item) {
  const raw = item?.correctRate;
  if (raw === undefined || raw === null || String(raw).trim() === "")
    return "—";

  const s = String(raw).trim();
  const hasPercent = s.includes("%");
  const numPart = Number(s.replace("%", "").replace(",", ".").trim());
  if (Number.isNaN(numPart)) return s;

  let pct = numPart;
  if (!hasPercent && pct > 0 && pct <= 1) pct *= 100;

  const rounded = Math.round(pct * 10) / 10;
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
}

/**
 * @typedef {Object} MappedRow
 * @property {any} key
 * @property {number} [stt]
 * @property {string} username
 * @property {string} predictionsPlayedDisplay
 * @property {string} matchesDisplay
 * @property {string} accuracyDisplay
 */

const PlayHistoryList = () => {
  const location = useLocation();
  const initialUsername = consumeUsernameFilterForHistory(location);
  const [form] = Form.useForm();
  const [dataList, setDataList] = useState(/** @type {MappedRow[]} */ ([]));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIST_DEFAULT_PAGE_SIZE);
  const [totalRecord, setTotalRecord] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataFilter, setDataFilter] = useState(() =>
    buildUsernameDataFilter(initialUsername)
  );

  const locale = { emptyText: TEXT_DEF.text0035 };

  useEffect(() => {
    if (initialUsername) {
      form.setFieldValue("usernameFilter", initialUsername);
    }
  }, [form, initialUsername]);

  useEffect(() => {
    getListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, dataFilter]);

  const buildQuery = () => {
    const qs = [buildPageQuery(page, pageSize).query];
    if (dataFilter && dataFilter.trim() !== "") qs.push(dataFilter);
    return qs.join("&");
  };

  const getListData = async () => {
    setLoading(true);
    try {
      const resp = await API.get(`${PLAY_HIS}?${buildQuery()}`);
      const payload = resp?.data?.data;

      const list = Array.isArray(payload?.data) ? payload.data : [];
      const mapped = list.map((/** @type {PlayRowRaw} */ item) => ({
        key: item?.id,
        stt: item?.stt,
        username: item?.username,
        predictionsPlayedDisplay: pickPredictionsPlayedDisplay(item),
        matchesDisplay: formatMatchCountDisplay(item),
        accuracyDisplay: formatCorrectRateDisplay(item),
      }));

      setDataList(mapped);
      const total = parseTotalFromPayload(payload, mapped.length);
      setTotalRecord(total);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const onChangePage = createPageChangeHandler(
    page,
    pageSize,
    setPage,
    setPageSize
  );

  const onSearch = () => {
    let params = "";
    const usernameVal = form.getFieldValue("usernameFilter");

    if (usernameVal)
      params += `username=${String(usernameVal).trim().toLowerCase()}`;

    setPage(1);
    setDataFilter(params !== "" ? params : null);
  };

  const dataColums = [
    { title: "STT", dataIndex: "stt", key: "stt" },
    { title: TEXT_DEF.text0002, dataIndex: "username", key: "username" },
    {
      title: "Số lượt đã chơi",
      key: "predictionsPlayedDisplay",
      dataIndex: "predictionsPlayedDisplay",
      align: "center",
    },
    {
      title: "Số trận",
      key: "matchesDisplay",
      dataIndex: "matchesDisplay",
      align: "center",
    },
    {
      title: "Tỷ lệ đúng",
      key: "accuracyDisplay",
      dataIndex: "accuracyDisplay",
      align: "center",
    },
  ];

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const qs = dataFilter && dataFilter.trim() !== "" ? `?${dataFilter}` : "";
      const response = await API.post(
        `${PLAY_HIS_EXCEL}${qs}`,
        {},
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const disposition = response.headers?.["content-disposition"];
      let fileName = "lich_su_user_tham_gia.xlsx";
      if (disposition && disposition.includes("filename=")) {
        fileName = disposition.split("filename=")[1].replace(/"/g, "");
      }
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const renderHistory = () => (
    <>
      <div className="col-12">
        <Form form={form} layout="inline" autoComplete="off" onFinish={onSearch}>
          <div className="col-12" style={{ marginBottom: 15, display: "flex" }}>
            <div className="col-5">
              <Form.Item name="usernameFilter" label={TEXT_DEF.text0002}>
                <Input style={{ width: "100%" }} />
              </Form.Item>
            </div>
          </div>

          <div className="payment-search">
            <Button htmlType="submit" type="primary">
              {TEXT_DEF.text0031}
            </Button>
            <Button
              style={{ marginLeft: 20 }}
              onClick={handleDownloadExcel}
              htmlType="button"
              type="primary"
            >
              {TEXT_DEF.text0050}
            </Button>
          </div>
        </Form>
      </div>

      <Spin spinning={loading} delay={500} size="large">
        <div style={{ display: "flex", width: "100%", marginTop: 10 }}>
          <Table
            key="playHistoryList"
            locale={locale}
            columns={dataColums}
            dataSource={dataList}
            pagination={false}
            bordered
            style={{ width: "100%" }}
          />
        </div>
        <ListPagination
          current={page}
          total={totalRecord}
          pageSize={pageSize}
          onChange={onChangePage}
          unit="dữ liệu"
        />
      </Spin>
    </>
  );

  return <div className="col-12">{renderHistory()}</div>;
};

export default PlayHistoryList;
